import { BaseNumber, CheckedAddress, raise } from '@sparkdotfi/common-universal'
import { maxUint256 } from 'viem'

import { ActionConfig, ActionContext } from '../../../logic/types'
import { WithdrawAction } from '../types'
import { poolAbi } from '@/config/abis/poolAbi'
import { lendingPoolConfig, wethGatewayConfig } from '@/config/contracts-generated'
import { trackTransactionConfirmation } from '@/domain/analytics/cookie3'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { getBalancesQueryKeyPrefix } from '@/domain/wallet/getBalancesQueryKeyPrefix'
import { allowanceQueryKey } from '@/features/actions/flavours/approve/logic/query'
import { ensureConfigTypes } from '@/features/actions/logic/transactions/useWrite'
import { aaveDataLayerQueryKey } from '@/features/sparklend/common/data/market-info/aave-data-layer/query'

export function createWithdrawActionConfig(action: WithdrawAction, context: ActionContext): ActionConfig {
  const { account, chainId } = context

  const wethGatewayAddress = getContractAddress(wethGatewayConfig.address, chainId)
  const lendingPoolAddress = getContractAddress(lendingPoolConfig.address, chainId)
  const withdrawTokenAddress = action.token.address

  const marketInfo = context.marketInfo ?? raise('Market info is required for withdraw action')
  const reserve = marketInfo.findOneReserveBySymbol(action.token.symbol)
  const aTokenAddress = reserve.aToken.address

  return {
    getWriteConfig: () => {
      const withdrawAmount = (action.all ? BaseNumber(maxUint256) : action.token.toBaseNumber(action.value)).toBigInt()

      if (withdrawTokenAddress === CheckedAddress.EEEE()) {
        return ensureConfigTypes({
          address: wethGatewayAddress,
          abi: wethGatewayConfig.abi,
          functionName: 'withdrawETH',
          args: [lendingPoolAddress, withdrawAmount, account],
        })
      }

      return ensureConfigTypes({
        abi: poolAbi,
        address: lendingPoolAddress,
        functionName: 'withdraw',
        args: [withdrawTokenAddress, withdrawAmount, account],
      })
    },

    invalidates: () => [
      allowanceQueryKey({ token: aTokenAddress, spender: wethGatewayAddress, account, chainId }),
      getBalancesQueryKeyPrefix({ chainId, account }),
      aaveDataLayerQueryKey({ chainId, account }),
    ],

    onSuccessfulAction: () => {
      trackTransactionConfirmation({
        name: 'Borrow_Withdraw_Confirmation',
        value: action.token.toUSD(action.value).toNumber(),
        isInSandbox: Boolean(context.isInSandbox),
      })
    },
  }
}
